/**
 * `/contact` — the one contact page for all three archives. (BL-0096)
 *
 * A route (`config.addonRoutes`), not Plone content: no `contact` object
 * exists on any of the three sites, so the backend content fetch would 404.
 * Same pattern as `/privacy` and the version-history hub.
 *
 * WHY A FORM RATHER THAN THE ADDRESS IT REPLACES. A mailto discards everything
 * that makes a report actionable — which archive, which document, what the
 * person was doing, what their browser was. On-site search over 28 days is
 * almost entirely bare document identifiers, so the dominant unmet need is "I
 * cannot find this" and "this looks wrong": exactly the reports that should
 * become prc-maintenance corrections and backlog tickets, and exactly what a
 * paragraph of prose in an inbox makes expensive.
 *
 * THE CATEGORY IS FIRST AND DRIVES THE REST. It is the single answer that
 * decides what else is worth asking. See ./categories.js.
 *
 * SSR CONTRACT. The whole form renders server-side: real `<form>`, real
 * `<label>`s, real options, and the fallback address as a real `<a href>`. Only
 * the submit handler and the puzzle container are client-side, and none of the
 * server-rendered markup varies by captcha state, consent state or anything
 * else per-visitor — the edge HTML cache is shared across every visitor, so it
 * must not.
 *
 * NO ACKNOWLEDGEMENT EMAIL, deliberately. Nothing is ever sent to an address
 * typed into this form: that is the difference between a contact form and a
 * mail amplifier pointed at whoever a stranger names. The reference appears on
 * screen and only on screen.
 *
 * STATE IS LOCAL, NOT REDUX. The plan called for an action + reducer pair; on
 * writing it there is nothing for them to do. No other component reads the
 * submission, there is no SSR prefetch, and the request cannot go through
 * Volto's api middleware anyway — it has to be `AwsWafIntegration.fetch` so
 * the WAF token rides along and the 405 status stays readable. A slice would
 * have been ceremony.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from '@plone/volto/helpers';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { CONTACT_EMAIL } from '../../config/waf';
import { CATEGORIES, CATEGORY_BY_ID, FIELD } from './categories';
import {
  loadWafSdk,
  prefetchToken,
  renderPuzzle,
  submitContact,
} from './captcha';
import { previousRoute } from './routeTrail';

const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const MESSAGE_LIMIT = 8000;

/** Same loose rule the backend applies: catch a typo, refuse anything that is
 *  not a single address, and leave deliverability to the mail system. */
const emailLooksValid = (value) =>
  Boolean(value) &&
  value.length <= 254 &&
  !/[\s<>,;]/.test(value) &&
  /^[^@]+@[^@]+\.[^@.]+$/.test(value);

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** What the page can say about itself. Nothing here is consent-gated and
 *  nothing identifies a person: no analytics id, no cookies, no fingerprint. */
const describeEnvironment = () => {
  if (typeof window === 'undefined') return {};
  const out = {};
  try {
    out.viewport = `${window.innerWidth}x${window.innerHeight}`;
    out.screen = `${window.screen.width}x${window.screen.height}`;
    out.pixelRatio = String(window.devicePixelRatio || 1);
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    out.colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    /* any of these can throw in a hardened browser; partial is fine */
  }
  return out;
};

const ContactForm = () => {
  const site = useSiteConfig();
  const location = useLocation();
  const params = new URLSearchParams((location && location.search) || '');

  const requestedTopic = params.get('topic') || '';
  const initialCategory = CATEGORY_BY_ID[requestedTopic] ? requestedTopic : '';
  const prefillDocid = params.get('docid') || '';
  const prefillPath = params.get('path') || '';
  const prefillQuery = params.get('q') || '';

  const [category, setCategory] = React.useState(initialCategory);
  const [subcategory, setSubcategory] = React.useState('');
  const [docid, setDocid] = React.useState(prefillDocid);
  const [searchQuery, setSearchQuery] = React.useState(prefillQuery);
  const [message, setMessage] = React.useState('');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [attachments, setAttachments] = React.useState([]);
  const [attachmentError, setAttachmentError] = React.useState('');
  const [status, setStatus] = React.useState('idle');
  const [error, setError] = React.useState('');
  const [reference, setReference] = React.useState('');
  const [needsPuzzle, setNeedsPuzzle] = React.useState(false);
  const puzzleRef = React.useRef(null);

  const active = CATEGORY_BY_ID[category];
  const origin = (site.baseUrl || '').replace(/\/+$/, '');
  const canonical = `${origin}/contact`;
  const title = `Contact us — ${site.title}`;
  const description = `Report a problem with a document, a broken page or a search that found nothing on ${site.title}, or ask a question.`;
  const documentUrl = prefillPath ? `${origin}${prefillPath}` : '';

  // Acquire the WAF token while the visitor types, so the Challenge rule is
  // invisible by the time they press Send. Failure is silent by design: the
  // submit path falls back to plain fetch and reports honestly if that is
  // stopped.
  React.useEffect(() => {
    let cancelled = false;
    loadWafSdk().then((ready) => {
      if (!cancelled && ready) prefetchToken();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    setAttachmentError('');
    if (!files.length) {
      setAttachments([]);
      return;
    }
    if (files.length > MAX_ATTACHMENTS) {
      setAttachmentError(`Attach at most ${MAX_ATTACHMENTS} images.`);
      event.target.value = '';
      setAttachments([]);
      return;
    }
    const tooBig = files.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      setAttachmentError(`“${tooBig.name}” is larger than 5 MB.`);
      event.target.value = '';
      setAttachments([]);
      return;
    }
    const notImage = files.find((f) => !/^image\//.test(f.type));
    if (notImage) {
      setAttachmentError(`“${notImage.name}” is not an image.`);
      event.target.value = '';
      setAttachments([]);
      return;
    }
    try {
      const read = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          size: file.size,
          data: await readAsDataUrl(file),
        })),
      );
      setAttachments(read);
    } catch {
      setAttachmentError('That file could not be read.');
      setAttachments([]);
    }
  };

  const buildPayload = () => ({
    category,
    subcategory: subcategory || '',
    reporter_name: name,
    reporter_email: email,
    message,
    docid: active && active.fields.includes(FIELD.DOC) ? docid : '',
    search_query:
      active && active.fields.includes(FIELD.QUERY) ? searchQuery : '',
    document_url: documentUrl,
    // An explicit prefill link wins; the route trail is only the fallback.
    came_from: prefillPath || previousRoute(),
    environment: describeEnvironment(),
    website,
    attachments: attachments.map(({ filename, data }) => ({ filename, data })),
  });

  // `attempt` bounds the two retries below. Both the challenge and the puzzle
  // path re-enter this function, and a server that kept answering the same way
  // would otherwise spin forever behind a "Sending…" button.
  const send = async (payload, attempt = 0) => {
    let response;
    try {
      response = await submitContact(payload);
    } catch {
      setStatus('error');
      setError(
        'We could not reach the server. Please check your connection and try again.',
      );
      return;
    }

    // 202 is the WAF's silent Challenge, unsatisfied — measured from a
    // token-less POST on 2026-08-29: `202` with `x-amzn-waf-action:
    // challenge`, terminated at the ALB, nothing reaching Plone.
    // `AwsWafIntegration.fetch` normally solves and retries this without us
    // ever seeing it, so arriving here means the SDK never loaded (blocked
    // script, offline) or its token was refused. Try once to acquire one, then
    // stop and say so — there is no puzzle to offer for a challenge.
    if (response.status === 202) {
      if (attempt === 0 && (await prefetchToken())) {
        await send(payload, attempt + 1);
        return;
      }
      setStatus('error');
      setError(
        'Your browser could not be verified, which usually means a script blocker. Please allow scripts on this page and try again, or email us instead.',
      );
      return;
    }

    // 405 is AWS WAF asking for a puzzle, not an error. See ./captcha.js.
    if (response.status === 405) {
      if (attempt > 1) {
        setStatus('error');
        setError(
          'We could not confirm you are a person. Please email us instead.',
        );
        return;
      }
      setStatus('puzzle');
      setNeedsPuzzle(true);
      const solved = await renderPuzzle(puzzleRef.current);
      setNeedsPuzzle(false);
      if (!solved) {
        setStatus('error');
        setError(
          'We could not confirm you are a person. Please try again, or email us instead.',
        );
        return;
      }
      setStatus('sending');
      await send(payload, attempt + 1);
      return;
    }

    if (response.status === 201) {
      let body = {};
      try {
        body = await response.json();
      } catch {
        /* a 201 we cannot parse is still a 201 */
      }
      setReference(body.reference || '');
      setStatus('sent');
      return;
    }

    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    setStatus('error');
    setError(
      (body && body.message) ||
        'Your message was not accepted. Please try again, or email us instead.',
    );
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!active) {
      setError('Choose what your message is about.');
      return;
    }
    if (message.trim().length < 10) {
      setError('Please tell us a little more about the problem.');
      return;
    }
    if (!emailLooksValid(email)) {
      setError('Enter an email address we can reply to.');
      return;
    }
    setStatus('sending');
    await send(buildPayload());
  };

  const busy = status === 'sending' || status === 'puzzle';

  return (
    <div className="pp-contact" id="view">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        {/* BL-0157 (TAIUIT seo/twitter-card): same card type DocumentSEO and
            SiteSEO emit. */}
        <meta name="twitter:card" content="summary" />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content={site.title} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <div className="prc-kit__main">
        <article className="prc-kit__doc">
          <h1 className="prc-kit__doc-h1">Contact us</h1>

          {status === 'sent' ? (
            <div className="pp-contact__done" role="status">
              <h2>Thank you — your message has been received.</h2>
              {reference ? (
                <p>
                  Your reference is <strong>{reference}</strong>. Quote it if
                  you write to us about this again.
                </p>
              ) : null}
              <p>
                We read everything that arrives here. We do not send an
                automatic acknowledgement, so this page is the only place that
                reference appears — copy it now if you want it.
              </p>
              <p>
                <Link to="/">Back to {site.title}</Link>
              </p>
            </div>
          ) : (
            <>
              <p className="pp-contact__lede">
                This archive is maintained by people who want it to be right. If
                a document here disagrees with the document itself, if a page is
                broken, or if you searched for something that should have been
                found, tell us and we will look at it.
              </p>

              <form className="pp-contact__form" onSubmit={onSubmit} noValidate>
                <fieldset className="pp-contact__fieldset">
                  <legend className="pp-contact__legend">
                    What is this about?
                  </legend>
                  <ul className="pp-contact__topics">
                    {CATEGORIES.map((option) => (
                      <li key={option.id} className="pp-contact__topic">
                        <input
                          type="radio"
                          id={`pp-topic-${option.id}`}
                          name="category"
                          value={option.id}
                          checked={category === option.id}
                          onChange={() => {
                            setCategory(option.id);
                            setSubcategory('');
                          }}
                        />
                        <label htmlFor={`pp-topic-${option.id}`}>
                          <span className="pp-contact__topic-label">
                            {option.label}
                          </span>
                          {option.blurb ? (
                            <span className="pp-contact__topic-blurb">
                              {option.blurb}
                            </span>
                          ) : null}
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>

                {/* One grid for every field. Two columns where there is room,
                    one on a narrow screen; the long controls opt out with
                    --wide. Before this the form was capped at 62ch inside a
                    940px column, which left half the page empty and made the
                    eight categories an eight-screen scroll. */}
                <div className="pp-contact__grid">
                  {active && active.subcategories.length ? (
                    <div className="pp-contact__field">
                      <label htmlFor="pp-subcategory">
                        {active.subcategoryLabel || 'Which of these is it?'}
                      </label>
                      <select
                        id="pp-subcategory"
                        name="subcategory"
                        value={subcategory}
                        onChange={(e) => setSubcategory(e.target.value)}
                      >
                        <option value="">Not sure / not listed</option>
                        {active.subcategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  {active && active.fields.includes(FIELD.DOC) ? (
                    <div className="pp-contact__field">
                      <label htmlFor="pp-docid">Document number</label>
                      <input
                        type="text"
                        id="pp-docid"
                        name="docid"
                        value={docid}
                        placeholder="DOE O 433.1B"
                        aria-describedby="pp-docid-hint"
                        onChange={(e) => setDocid(e.target.value)}
                      />
                      <p className="pp-contact__hint" id="pp-docid-hint">
                        If you know it. Leave it blank if you do not.
                      </p>
                    </div>
                  ) : null}

                  {active && active.fields.includes(FIELD.QUERY) ? (
                    <div className="pp-contact__field">
                      <label htmlFor="pp-query">What did you search for?</label>
                      <input
                        type="text"
                        id="pp-query"
                        name="search_query"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  ) : null}

                  <div className="pp-contact__field pp-contact__field--wide">
                    <label htmlFor="pp-message">
                      {(active && active.messageLabel) || 'Your message'}{' '}
                      <span className="pp-contact__required">(required)</span>
                    </label>
                    <textarea
                      id="pp-message"
                      name="message"
                      rows={8}
                      required
                      maxLength={MESSAGE_LIMIT}
                      value={message}
                      aria-describedby="pp-message-hint"
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    <p className="pp-contact__hint" id="pp-message-hint">
                      {(active && active.messageHint) ||
                        'Please be as specific as you can.'}
                    </p>
                  </div>

                  <div className="pp-contact__field">
                    <label htmlFor="pp-name">Your name</label>
                    <input
                      type="text"
                      id="pp-name"
                      name="reporter_name"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="pp-contact__field">
                    <label htmlFor="pp-email">
                      Your email{' '}
                      <span className="pp-contact__required">(required)</span>
                    </label>
                    <input
                      type="email"
                      id="pp-email"
                      name="reporter_email"
                      autoComplete="email"
                      required
                      value={email}
                      aria-describedby="pp-email-hint"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <p className="pp-contact__hint" id="pp-email-hint">
                      So we can ask a follow-up question or tell you what we
                      found. We never send anything else to it.
                    </p>
                  </div>

                  <div className="pp-contact__field">
                    <label htmlFor="pp-files">Screenshots (optional)</label>
                    <input
                      type="file"
                      id="pp-files"
                      name="attachments"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      multiple
                      aria-describedby="pp-files-hint"
                      onChange={onFiles}
                    />
                    <p className="pp-contact__hint" id="pp-files-hint">
                      Up to {MAX_ATTACHMENTS} images, 5 MB each. We strip the
                      camera and location data before storing them.
                    </p>
                    {attachmentError ? (
                      <p className="pp-contact__error" role="alert">
                        {attachmentError}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Honeypot. Off-screen rather than display:none, unlabelled to
                    a person and unreachable by keyboard, but a form filler
                    reads it as a plausible field and completes it. */}
                <div className="pp-contact__hp" aria-hidden="true">
                  <label htmlFor="pp-website">Website</label>
                  <input
                    type="text"
                    id="pp-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                {error ? (
                  <p className="pp-contact__error" role="alert">
                    {error}
                  </p>
                ) : null}

                {/* The puzzle draws in here, in place, with the filled form
                    still on screen behind it. Kept in the DOM at all times so
                    the SDK always has a container to draw into. */}
                <div
                  className={
                    needsPuzzle
                      ? 'pp-contact__puzzle pp-contact__puzzle--active'
                      : 'pp-contact__puzzle'
                  }
                  ref={puzzleRef}
                />

                <p className="pp-contact__note">
                  Your message arrives with the context needed to act on it —
                  which archive you were on, the page you came from, and your
                  browser and network details. The{' '}
                  <Link to="/privacy">privacy notice</Link> lists it in full and
                  says how long we keep it.
                </p>

                <div className="pp-contact__actions">
                  <button
                    type="submit"
                    className="pp-contact__btn"
                    disabled={busy}
                  >
                    {busy ? 'Sending…' : 'Send message'}
                  </button>
                  <p className="pp-contact__status" aria-live="polite">
                    {status === 'puzzle'
                      ? 'One moment — please confirm you are a person.'
                      : ''}
                  </p>
                </div>
              </form>

              <p className="pp-contact__fallback">
                Prefer email, or cannot use this form?{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> reaches
                the same people.
              </p>
              <noscript>
                <p className="pp-contact__error">
                  This form needs JavaScript to send. Please email{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
                  instead.
                </p>
              </noscript>
            </>
          )}
        </article>
      </div>
    </div>
  );
};

export default ContactForm;
